import React, { useEffect, useRef } from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { themeTokens } from '@croquiscom-pvt/zds/tokens/zigzag';
import styled from '@emotion/styled';

import { useOnceReveal } from '@common/hooks';
import { BaseText } from '@common/toolbox';
import { displayPrice } from '@common/utils';

import { PdpDeliveryType } from '../../../graphql/select-option_types.2';
import { OptionType, SelectBoxItemOptionValue } from '../select-box-item';
import { SelectBoxItemPreview } from '../select-box-item-preview';
import { ShippingLogo } from './ShippingLogo';

export interface SoldOutOptionValue extends SelectBoxItemOptionValue {
  notification_info?: {
    product_item_id: string;
    active: boolean;
  };
}

interface Props {
  option: SoldOutOptionValue;
  disabledPrice?: boolean;
  isLastOption: boolean;
  isFirstSoldOutOption: boolean;
  is_zigzin_product?: boolean;
  isMatchingProduct?: boolean;
  hasFastShippingOption?: boolean;
  onSelect: (option: SelectBoxItemOptionValue) => void;
  onImpress?: (value: string) => void;
  shouldShowButton: boolean;
  onRestockNotiClick: (option: SelectBoxItemOptionValue, active: boolean) => void;
}

const getSoldOutTextSize = (language: 'en' | 'ko' | 'ja') => {
  const sizes: Record<'en' | 'ko' | 'ja', number> = {
    en: 60,
    ko: 32,
    ja: 58,
  } as Record<string, number>;

  return sizes[language] ?? 32;
};

export const SoldOutSelectBoxItem = ({
  option,
  disabledPrice,
  isLastOption,
  isFirstSoldOutOption,
  is_zigzin_product,
  isMatchingProduct,
  hasFastShippingOption,
  shouldShowButton,
  onSelect,
  onImpress,
  onRestockNotiClick,
}: Props) => {
  const {
    value,
    code,
    image_url,
    webp_image_url,
    notification_info,
    option_type,
    price,
    delivery_type,
    is_zigzin,
    is_last_option,
    is_free_shipping,
    shipping_fee,
  } = option;
  const { targetRef, isRevealed } = useOnceReveal();
  const language = 'ko';
  const containerRef = useRef<HTMLLIElement>(null);

  const visible_preview = !!(code || image_url || webp_image_url);
  const active = notification_info?.active ?? false;

  const is_additional_item = option_type === OptionType.ADDITIONAL;
  const isInFirstDepthDrawer = isFirstSoldOutOption || !shouldShowButton;
  const is_fast_shipping = delivery_type === PdpDeliveryType.FAST;
  const is_store_zigzin = delivery_type === PdpDeliveryType.VENDOR_FLEX;
  const isGeneralMatchingProduct = isMatchingProduct && !is_zigzin;
  const isMatchedGeneralProduct = Boolean(isMatchingProduct && !is_zigzin_product);

  const displayShippingFee =
    (isMatchingProduct || hasFastShippingOption) &&
    !is_fast_shipping &&
    is_last_option &&
    !!shipping_fee &&
    !is_free_shipping;
  const displayShippingTag =
    is_zigzin || is_store_zigzin || isGeneralMatchingProduct || displayShippingFee || hasFastShippingOption;
  const visiblePrice =
    !disabledPrice &&
    !!price &&
    (is_additional_item || isGeneralMatchingProduct || (!is_zigzin && hasFastShippingOption));

  const handleClick = () => {
    if (shouldShowButton && !isLastOption && !isFirstSoldOutOption) {
      onSelect(option);
    }
  };

  useEffect(() => {
    // MEMO: 재입고 알림 버튼은 직진 상품인 경우에만 노출되며, 알림 신청이 되어있지 않는 경우에만 로그 전송
    if (isRevealed && shouldShowButton && !active) {
      onImpress?.(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevealed]);

  return (
    <Container
      ref={containerRef}
      onClick={handleClick}
      data-allow-preview
      has_preview={visible_preview}
      padding={Boolean(isMatchedGeneralProduct || is_zigzin_product || hasFastShippingOption)}
    >
      {visible_preview && (
        <PreviewContainer>
          <SelectBoxItemPreview code={code} image_url={image_url} webp_image_url={webp_image_url} />
        </PreviewContainer>
      )}
      <OptionTextContainer>
        <Flex is_opacity={isInFirstDepthDrawer}>
          <OptionTextInner>
            <OptionText
              variation='BODY_15_MEDIUM'
              color={
                isInFirstDepthDrawer ? themeTokens.v3Color.gray300.value.light : themeTokens.v3Color.gray900.value.light
              }
            >
              {value}
            </OptionText>
            {isInFirstDepthDrawer && (
              <SoldOutText
                variation='CAPTION_12_SEMIBOLD'
                color={themeTokens.v3Color.gray300.value.light}
                size={getSoldOutTextSize(language)}
              >
                · 품절
              </SoldOutText>
            )}
          </OptionTextInner>
          {displayShippingTag && (
            <ShippingTypeTag>
              <ShippingLogo
                is_store_zigzin={Boolean(is_store_zigzin)}
                isZigzin={Boolean(is_zigzin)}
                isFastShipping={is_fast_shipping}
                isGeneralMatchingProduct={Boolean(isGeneralMatchingProduct)}
                hasFastShippingOption={Boolean(hasFastShippingOption)}
              />
              {displayShippingFee && <FeeTag> (+배송 {displayPrice(shipping_fee)})</FeeTag>}
            </ShippingTypeTag>
          )}
        </Flex>
      </OptionTextContainer>
      {visiblePrice && <PriceText>{displayPrice(price)}</PriceText>}
      {shouldShowButton && (
        <RestockHolder>
          {isLastOption && (
            <RestockNotiButton ref={targetRef} onClick={() => onRestockNotiClick(option, !active)}>
              <RestockButton variation='CAPTION_12_SEMIBOLD' color={themeTokens.v3Color.gray700.value.light}>
                {active ? '알림 취소' : '재입고 알림'}
              </RestockButton>
            </RestockNotiButton>
          )}
          {!isLastOption && isFirstSoldOutOption && (
            <ViewMoreButton onClick={() => onSelect(option)}>
              <RestockButton
                has_underline
                variation='CAPTION_12_SEMIBOLD'
                color={themeTokens.v3Color.gray700.value.light}
              >
                재입고 알림
              </RestockButton>
            </ViewMoreButton>
          )}
        </RestockHolder>
      )}
    </Container>
  );
};

const Container = styled.li<{ has_preview: boolean; padding: boolean }>`
  border-top: 1px solid ${vars.v3Color.gray100};
  border-radius: 0;
  position: relative;
  display: flex;
  align-items: center;
  padding: 17px 16px;

  ${({ has_preview }) =>
    has_preview &&
    `
    padding: 8px 16px;
  `}

  ${({ padding }) => padding && 'padding: 17px 16px;'}
`;

const PreviewContainer = styled.div`
  margin-left: -4px;
  margin-right: 8px;
  align-self: center;
`;

const OptionTextContainer = styled.div`
  padding: 2px 0;
  display: flex;
  flex: 1;
`;

const OptionTextInner = styled.span`
  position: relative;
  display: flex;
`;

const OptionText = styled(BaseText)`
  word-break: break-all;
`;

const SoldOutText = styled(BaseText)<{ size: number }>`
  position: relative;
  top: 2px;
  text-align: center;
  min-width: ${({ size }) => `${size}px`};
  white-space: pre;
`;

const RestockNotiButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 16px;
  display: flex;
  justify-content: center;
  padding: 7px 12px;
  border-radius: 32px;
  border: 1px solid ${vars.v3Color.gray100};
  background-color: ${vars.v3Color.white};
  z-index: 5;
`;

const ViewMoreButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 16px;
  display: flex;
  align-items: flex-end;
  padding: 7px 0;
`;

const RestockButton = styled(BaseText)<{ has_underline?: boolean }>`
  line-height: inherit;

  ${({ has_underline }) =>
    has_underline &&
    `
    &::after {
      content: '';
      display: block;
      width: 100%;
      border-bottom: 0.5px solid ${vars.v3Color.gray300};
    }
  `}
`;

const PriceText = styled.span`
  padding: 2px 0 0 20px;
  align-self: start;
  margin-left: auto;
  font-size: 15px;
  font-weight: 500;
  color: ${vars.v3Color.gray200};
`;

const RestockHolder = styled.div`
  min-width: 82px;
`;

const Flex = styled.span<{ is_opacity: boolean }>`
  display: flex;
  flex-direction: column;

  svg {
    ${({ is_opacity }) => is_opacity && 'opacity: 0.3'};
  }
`;

const ShippingTypeTag = styled.span`
  margin: 4px 0 0 0;
  width: 100%;
  display: flex;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  color: ${vars.v3Color.gray200};
  white-space: break-spaces;
`;

const FeeTag = styled.span`
  font-weight: 400;
`;
